/**
 * RENA.STUDIO THEME JAVASCRIPT
 * Minimal, Editorial, Drop-Based Fashion Brand
 * Vanilla JS only, no jQuery
 */

/* ===================================================
   UTILITY FUNCTIONS
   =================================================== */

/**
 * Check if element is visible in viewport
 */
function isElementVisible(element) {
  if (!element) return false;
  const rect = element.getBoundingClientRect();
  return (
    rect.top < window.innerHeight &&
    rect.bottom > 0 &&
    rect.left < window.innerWidth &&
    rect.right > 0
  );
}

/**
 * Safely parse JSON
 */
function parseJSON(str) {
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
}

/**
 * Throttle function for scroll/resize events
 */
function throttle(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Debounce function
 */
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Show toast notification
 */
function showToast(message, type = 'success') {
  const toast = document.createElement('div');
  toast.className = `rena-toast ${type}`;
  toast.textContent = message;
  document.body.appendChild(toast);

  setTimeout(() => {
    toast.style.animation = 'slideDown 300ms ease-out';
    setTimeout(() => toast.remove(), 300);
  }, 3000);
}

/* ===================================================
   COUNTDOWN TIMER
   =================================================== */

class CountdownTimer {
  constructor(targetDate, elementSelector) {
    this.targetDate = new Date(targetDate).getTime();
    this.element = document.querySelector(elementSelector);
    this.animationFrameId = null;

    if (this.element) {
      this.start();
    }
  }

  start() {
    this.update();
    this.animationFrameId = setInterval(() => this.update(), 1000);
  }

  update() {
    const now = new Date().getTime();
    const distance = this.targetDate - now;

    if (distance < 0) {
      this.onExpired();
      return;
    }

    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);

    this.render(days, hours, minutes, seconds);
  }

  render(days, hours, minutes, seconds) {
    if (!this.element) return;

    const format = (num) => String(num).padStart(2, '0');
    const daysEl = this.element.querySelector('[data-timer="days"]');
    const hoursEl = this.element.querySelector('[data-timer="hours"]');
    const minutesEl = this.element.querySelector('[data-timer="minutes"]');
    const secondsEl = this.element.querySelector('[data-timer="seconds"]');

    if (daysEl) daysEl.textContent = format(days);
    if (hoursEl) hoursEl.textContent = format(hours);
    if (minutesEl) minutesEl.textContent = format(minutes);
    if (secondsEl) secondsEl.textContent = format(seconds);
  }

  onExpired() {
    clearInterval(this.animationFrameId);
    if (this.element) {
      this.element.classList.add('expired');
      this.element.innerHTML = '<p>This drop has ended</p>';
    }
  }

  destroy() {
    clearInterval(this.animationFrameId);
  }
}

/* ===================================================
   HEADER & NAVIGATION
   =================================================== */

class Header {
  constructor() {
    this.header = document.querySelector('.rena-header');
    this.menuToggle = document.querySelector('.rena-menu-toggle');
    this.mobileMenu = document.querySelector('.rena-mobile-menu');
    this.lastScrollTop = 0;

    this.init();
  }

  init() {
    if (!this.header) return;

    // Mobile menu toggle
    if (this.menuToggle) {
      this.menuToggle.addEventListener('click', () => this.toggleMobileMenu());
    }

    // Close menu on link click
    if (this.mobileMenu) {
      this.mobileMenu.querySelectorAll('.rena-mobile-menu-item').forEach((link) => {
        link.addEventListener('click', () => this.closeMobileMenu());
      });
    }

    // Close menu on outside click
    document.addEventListener('click', (e) => {
      if (this.mobileMenu?.classList.contains('active')) {
        if (!this.mobileMenu.contains(e.target) && !this.menuToggle.contains(e.target)) {
          this.closeMobileMenu();
        }
      }
    });

    // Scroll detection for header shrink
    let ticking = false;
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          requestAnimationFrame(() => this.handleScroll());
          ticking = true;
        }
      },
      { passive: true }
    );
  }

  toggleMobileMenu() {
    if (this.mobileMenu?.classList.contains('active')) {
      this.closeMobileMenu();
    } else {
      this.openMobileMenu();
    }
  }

  openMobileMenu() {
    this.mobileMenu?.classList.add('active');
    this.menuToggle?.classList.add('active');
    document.body.classList.add('menu-open');
  }

  closeMobileMenu() {
    this.mobileMenu?.classList.remove('active');
    this.menuToggle?.classList.remove('active');
    document.body.classList.remove('menu-open');
  }

  handleScroll() {
    const currentScroll = window.scrollY;

    if (currentScroll > 100) {
      this.header?.classList.add('shrink');
    } else {
      this.header?.classList.remove('shrink');
    }

    this.lastScrollTop = currentScroll <= 0 ? 0 : currentScroll;
  }
}

/* ===================================================
   CART FUNCTIONALITY
   =================================================== */

class Cart {
  constructor() {
    this.cartDrawer = document.querySelector('.rena-cart-drawer');
    this.cartForm = document.querySelector('[data-cart-form]');
    this.addToCartButtons = document.querySelectorAll('[data-add-to-cart]');
    this.cartIcon = document.querySelector('[data-cart-icon]');
    this.cartBadge = document.querySelector('.rena-cart-badge');

    this.init();
  }

  init() {
    // Add to cart buttons
    this.addToCartButtons.forEach((button) => {
      button.addEventListener('click', (e) => this.handleAddToCart(e));
    });

    // Quantity controls
    document.querySelectorAll('[data-quantity-minus]').forEach((btn) => {
      btn.addEventListener('click', (e) => this.updateQuantity(e, -1));
    });

    document.querySelectorAll('[data-quantity-plus]').forEach((btn) => {
      btn.addEventListener('click', (e) => this.updateQuantity(e, 1));
    });

    // Remove from cart
    document.querySelectorAll('[data-remove-item]').forEach((btn) => {
      btn.addEventListener('click', (e) => this.removeItem(e));
    });

    // Cart drawer close
    document.querySelector('[data-cart-close]')?.addEventListener('click', () => {
      this.closeCart();
    });

    // Cart icon
    this.cartIcon?.addEventListener('click', () => this.openCart());

    this.updateCartBadge();
  }

  async handleAddToCart(event) {
    const button = event.target.closest('[data-add-to-cart]');
    if (!button) return;

    const variantId = button.dataset.variantId;
    const quantity = parseInt(button.dataset.quantity || 1);

    if (!variantId) {
      showToast('Please select a variant', 'error');
      return;
    }

    button.classList.add('loading');
    button.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: quantity,
            },
          ],
        }),
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      showToast('Added to cart!', 'success');
      this.updateCartBadge();
      this.openCart();
    } catch (error) {
      showToast('Error adding to cart', 'error');
      console.error('Add to cart error:', error);
    } finally {
      button.classList.remove('loading');
      button.disabled = false;
    }
  }

  async updateQuantity(event, change) {
    const button = event.target;
    const key = button.dataset.key;
    const input = button.parentElement?.querySelector('[data-quantity-input]');
    let quantity = parseInt(input?.value || 1) + change;

    if (quantity < 0) quantity = 0;

    if (quantity === 0) {
      this.removeItem(event);
      return;
    }

    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: parseInt(key),
          quantity: quantity,
        }),
      });

      if (input) input.value = quantity;
      this.updateCartBadge();
    } catch (error) {
      showToast('Error updating quantity', 'error');
      console.error('Quantity update error:', error);
    }
  }

  async removeItem(event) {
    const button = event.target.closest('[data-remove-item]');
    if (!button) return;

    const key = button.dataset.key;

    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: parseInt(key),
          quantity: 0,
        }),
      });

      button.closest('[data-cart-item]')?.remove();
      this.updateCartBadge();
      showToast('Item removed', 'success');
    } catch (error) {
      showToast('Error removing item', 'error');
      console.error('Remove item error:', error);
    }
  }

  async updateCartBadge() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const count = cart.item_count || 0;

      if (this.cartBadge) {
        if (count > 0) {
          this.cartBadge.textContent = count;
          this.cartBadge.style.display = 'flex';
        } else {
          this.cartBadge.style.display = 'none';
        }
      }
    } catch (error) {
      console.error('Cart badge update error:', error);
    }
  }

  openCart() {
    this.cartDrawer?.classList.add('open');
    document.body.style.overflow = 'hidden';
  }

  closeCart() {
    this.cartDrawer?.classList.remove('open');
    document.body.style.overflow = '';
  }
}

/* ===================================================
   PRODUCT PAGE
   =================================================== */

class ProductPage {
  constructor() {
    this.mainImageWrapper = document.querySelector('[data-product-main-image]');
    this.galleryThumbs = document.querySelectorAll('[data-thumbnail]');
    this.shareToggle = document.querySelector('[data-share-toggle]');
    this.shareMenu = document.querySelector('[data-share-menu]');
    this.copyLinkButtons = document.querySelectorAll('[data-copy-link]');
    this.quantityInput = document.querySelector('[data-product-quantity-input]');
    this.quantityMinus = document.querySelector('[data-product-quantity-minus]');
    this.quantityPlus = document.querySelector('[data-product-quantity-plus]');

    this.init();
  }

  init() {
    // Gallery thumbnails
    this.galleryThumbs.forEach((thumb) => {
      thumb.addEventListener('click', (e) => this.selectGalleryImage(e));
    });

    // Share menu toggle
    this.shareToggle?.addEventListener('click', () => this.toggleShareMenu());

    // Copy link
    this.copyLinkButtons.forEach((button) => {
      button.addEventListener('click', () => this.copyLink(button));
    });

    // Quantity stepper (product form only, not the cart drawer)
    this.quantityMinus?.addEventListener('click', () => this.stepQuantity(-1));
    this.quantityPlus?.addEventListener('click', () => this.stepQuantity(1));
  }

  selectGalleryImage(event) {
    const thumb = event.target.closest('[data-thumbnail]');
    if (!thumb) return;

    const imageUrl = thumb.dataset.productImage;
    const mainImg = this.mainImageWrapper?.querySelector('img');
    if (!imageUrl || !mainImg) return;

    // image_tag renders srcset; the browser prefers it over src,
    // so it must be cleared for the swap to actually display.
    mainImg.removeAttribute('srcset');
    mainImg.removeAttribute('sizes');
    mainImg.src = imageUrl;

    this.galleryThumbs.forEach((t) => t.classList.remove('active'));
    thumb.classList.add('active');
  }

  toggleShareMenu() {
    if (!this.shareMenu) return;
    const isHidden = this.shareMenu.hasAttribute('hidden');
    if (isHidden) {
      this.shareMenu.removeAttribute('hidden');
    } else {
      this.shareMenu.setAttribute('hidden', '');
    }
    this.shareToggle?.setAttribute('aria-expanded', String(isHidden));
  }

  async copyLink(button) {
    if (!navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast(button.dataset.copiedText || 'Copied', 'success');
    } catch (error) {
      console.error('Copy link error:', error);
    }
  }

  stepQuantity(change) {
    if (!this.quantityInput) return;
    const min = parseInt(this.quantityInput.min || '1', 10);
    const current = parseInt(this.quantityInput.value || '1', 10);
    const next = Math.max(min, current + change);
    this.quantityInput.value = next;
  }
}

/* ===================================================
   SCROLL REVEAL
   =================================================== */

class ScrollReveal {
  constructor() {
    this.elements = document.querySelectorAll('.reveal');
    this.options = {
      threshold: 0.05,
      rootMargin: '0px 0px -100px 0px',
    };

    if ('IntersectionObserver' in window) {
      this.init();
    } else {
      this.revealAll();
    }
  }

  init() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          this.observer.unobserve(entry.target);
        }
      });
    }, this.options);

    this.elements.forEach((element) => {
      // Check if already visible
      if (isElementVisible(element)) {
        element.classList.add('visible');
      } else {
        this.observer.observe(element);
      }
    });
  }

  revealAll() {
    this.elements.forEach((element) => {
      element.classList.add('visible');
    });
  }
}

/* ===================================================
   ACCORDION / FAQ
   =================================================== */

class Accordion {
  constructor(containerSelector = '.rena-accordion') {
    this.container = document.querySelector(containerSelector);
    this.items = document.querySelectorAll('.rena-accordion-item');

    this.init();
  }

  init() {
    this.items.forEach((item) => {
      const trigger = item.querySelector('.rena-accordion-trigger');
      trigger?.addEventListener('click', () => this.toggle(item));
    });
  }

  toggle(item) {
    const isOpen = item.classList.contains('open');

    if (isOpen) {
      this.close(item);
    } else {
      this.open(item);
    }
  }

  open(item) {
    // Close other items if single open only
    if (this.container?.dataset.singleOpen === 'true') {
      this.items.forEach((i) => {
        if (i !== item) {
          this.close(i);
        }
      });
    }

    item.classList.add('open');
    const content = item.querySelector('.rena-accordion-content');
    if (content) {
      content.style.maxHeight = content.scrollHeight + 'px';
    }
  }

  close(item) {
    item.classList.remove('open');
    const content = item.querySelector('.rena-accordion-content');
    if (content) {
      content.style.maxHeight = '';
    }
  }
}

/* ===================================================
   NEWSLETTER FORM
   =================================================== */

class NewsletterForm {
  constructor(formSelector = '[data-newsletter-form]') {
    this.form = document.querySelector(formSelector);

    if (this.form) {
      this.init();
    }
  }

  init() {
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));
  }

  async handleSubmit(event) {
    event.preventDefault();

    const input = this.form.querySelector('input[type="email"]');
    const button = this.form.querySelector('button[type="submit"]');

    if (!input?.value) {
      showToast('Please enter an email', 'error');
      return;
    }

    button.classList.add('loading');
    button.disabled = true;

    try {
      // Shopify's Mailchimp integration
      const response = await fetch(this.form.action, {
        method: 'POST',
        body: new FormData(this.form),
      });

      if (response.ok) {
        showToast('Thank you for subscribing!', 'success');
        this.form.reset();
      } else {
        showToast('Error subscribing. Please try again.', 'error');
      }
    } catch (error) {
      showToast('Error subscribing. Please try again.', 'error');
      console.error('Newsletter error:', error);
    } finally {
      button.classList.remove('loading');
      button.disabled = false;
    }
  }
}

/* ===================================================
   ANNOUNCEMENT BAR
   =================================================== */

class AnnouncementBar {
  constructor(barSelector = '.rena-announcement-bar') {
    this.bar = document.querySelector(barSelector);
    this.closeBtn = this.bar?.querySelector('[data-announcement-close]');
    this.storageKey = 'renaAnnouncementDismissed';

    if (this.bar) {
      this.init();
    }
  }

  init() {
    // Check if already dismissed
    if (localStorage.getItem(this.storageKey)) {
      this.bar.classList.add('hidden');
      return;
    }

    this.closeBtn?.addEventListener('click', () => this.dismiss());
  }

  dismiss() {
    this.bar.classList.add('hidden');
    localStorage.setItem(this.storageKey, 'true');
  }
}

/* ===================================================
   SEARCH FUNCTIONALITY
   =================================================== */

class Search {
  constructor(formSelector = '[data-search-form]') {
    this.form = document.querySelector(formSelector);
    this.input = this.form?.querySelector('input[type="search"]');

    if (this.form) {
      this.init();
    }
  }

  init() {
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));
  }

  handleSubmit(event) {
    event.preventDefault();

    const query = this.input.value.trim();
    if (!query) {
      showToast('Please enter a search term', 'error');
      return;
    }

    // Redirect to search results page
    window.location.href = `/search?q=${encodeURIComponent(query)}`;
  }
}

/* ===================================================
   LANGUAGE TOGGLE (i18n)
   =================================================== */

class LanguageToggle {
  constructor() {
    this.toggleButton = document.querySelector('[data-language-toggle]');
    this.currentLang = localStorage.getItem('renaLanguage') || 'en';

    if (this.toggleButton) {
      this.init();
    }
  }

  init() {
    this.toggleButton.addEventListener('click', () => this.toggleLanguage());
    this.setLanguage(this.currentLang);
  }

  toggleLanguage() {
    const newLang = this.currentLang === 'en' ? 'es' : 'en';
    this.setLanguage(newLang);
  }

  setLanguage(lang) {
    this.currentLang = lang;
    localStorage.setItem('renaLanguage', lang);

    // Update all i18n elements
    document.querySelectorAll('[data-i18n]').forEach((element) => {
      const key = element.dataset.i18n;
      const text = window.i18nStrings?.[key]?.[lang];

      if (text) {
        if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
          element.placeholder = text;
        } else {
          element.textContent = text;
        }
      }
    });

    // Update button state
    if (this.toggleButton) {
      this.toggleButton.dataset.currentLang = lang;
      this.toggleButton.textContent = lang === 'en' ? 'ES' : 'EN';
    }
  }
}

/* ===================================================
   FORM VALIDATION
   =================================================== */

class FormValidator {
  constructor(formSelector = 'form') {
    this.forms = document.querySelectorAll(formSelector);
    this.init();
  }

  init() {
    this.forms.forEach((form) => {
      form.addEventListener('submit', (e) => this.validateForm(e));
    });

    // Real-time validation
    document.querySelectorAll('input, textarea').forEach((field) => {
      field.addEventListener('blur', (e) => this.validateField(e.target));
    });
  }

  validateForm(event) {
    const form = event.target;
    let isValid = true;

    form.querySelectorAll('input, textarea').forEach((field) => {
      if (!this.validateField(field)) {
        isValid = false;
      }
    });

    if (!isValid) {
      event.preventDefault();
    }
  }

  validateField(field) {
    let isValid = true;
    let errorMessage = '';

    // Email validation
    if (field.type === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(field.value)) {
        isValid = false;
        errorMessage = 'Please enter a valid email';
      }
    }

    // Required fields
    if (field.required && !field.value.trim()) {
      isValid = false;
      errorMessage = 'This field is required';
    }

    // Min length
    if (field.minLength && field.value.length < field.minLength) {
      isValid = false;
      errorMessage = `Minimum ${field.minLength} characters`;
    }

    // Show/hide error message
    const errorElement = field.parentElement?.querySelector('.rena-form-error');
    if (errorElement) {
      if (isValid) {
        errorElement.style.display = 'none';
        field.classList.remove('invalid');
      } else {
        errorElement.textContent = errorMessage;
        errorElement.style.display = 'block';
        field.classList.add('invalid');
      }
    }

    return isValid;
  }
}

/* ===================================================
   NEWSLETTER SUBMIT FEEDBACK
   Shopify's post-subscribe redirect does not always carry
   the customer_posted flag back to the page (challenge
   pages, redirect variations). Persist a short-lived flag
   at submit time so the success state shows regardless.
   =================================================== */

function persistNewsletterFeedback() {
  const forms = ['hero-newsletter-form', 'newsletter-form']
    .map((id) => document.getElementById(id))
    .filter(Boolean);
  if (!forms.length) return;

  forms.forEach((form) => {
    form.addEventListener('submit', () => {
      try { sessionStorage.setItem('rena_nl_submitted', form.id + '|' + Date.now()); } catch (e) { /* private mode */ }
    });
  });

  // Two independent signals: the flag set at submit time, and the
  // customer_posted param (Shopify drops it after a captcha challenge,
  // so neither signal alone is reliable).
  let stamp = null;
  try { stamp = sessionStorage.getItem('rena_nl_submitted'); } catch (e) { /* private mode */ }
  const posted = new URLSearchParams(window.location.search).get('customer_posted') === 'true';

  let submittedId = null;
  if (stamp) {
    try { sessionStorage.removeItem('rena_nl_submitted'); } catch (e) { /* private mode */ }
    const [id, ts] = stamp.split('|');
    if (Date.now() - Number(ts) <= 600000) submittedId = id;
  }
  if (!submittedId && !posted) return;

  const submittedForm = document.getElementById(submittedId) || forms[0];

  // Server-rendered validation error: leave the form visible, just bring it into view
  if (submittedForm.querySelector('.rena-hero__newsletter-error, .rena-newsletter__error')) {
    setTimeout(() => submittedForm.scrollIntoView({ block: 'center', behavior: 'smooth' }), 100);
    return;
  }

  // One subscription: every signup module on the page flips to the success state
  forms.forEach((form) => {
    const success = form.querySelector('[data-nl-success]');
    if (success) success.removeAttribute('hidden');
    const fields = form.querySelector('.rena-form__group, .rena-newsletter__form');
    if (fields) fields.style.display = 'none';
  });
  setTimeout(() => submittedForm.scrollIntoView({ block: 'center', behavior: 'smooth' }), 100);
}

/* ===================================================
   LOCALIZED NATIVE FORM VALIDATION
   Browsers show validation bubbles in the BROWSER's UI
   language, not the page language. Override with
   messages matching the page locale.
   =================================================== */

function localizeValidationMessages() {
  const lang = (document.documentElement.lang || 'es').slice(0, 2);
  const messages = {
    es: {
      valueMissing: 'Este campo es obligatorio.',
      typeMismatchEmail: 'Ingresa un correo electrónico válido.',
      generic: 'Revisa este campo.',
    },
    en: {
      valueMissing: 'This field is required.',
      typeMismatchEmail: 'Please enter a valid email address.',
      generic: 'Please check this field.',
    },
  };
  const t = messages[lang] || messages.es;

  document.querySelectorAll('input, textarea, select').forEach((field) => {
    field.addEventListener('invalid', () => {
      if (field.validity.valueMissing) {
        field.setCustomValidity(t.valueMissing);
      } else if (field.validity.typeMismatch && field.type === 'email') {
        field.setCustomValidity(t.typeMismatchEmail);
      } else if (!field.validity.customError) {
        field.setCustomValidity(t.generic);
      }
    });
    field.addEventListener('input', () => field.setCustomValidity(''));
  });
}

/* ===================================================
   INITIALIZATION
   =================================================== */

document.addEventListener('DOMContentLoaded', () => {
  // Initialize all components
  new Header();
  new Cart();
  new ProductPage();
  new ScrollReveal();
  new Accordion();
  new NewsletterForm();
  new AnnouncementBar();
  new Search();
  new LanguageToggle();
  new FormValidator();
  localizeValidationMessages();
  persistNewsletterFeedback();

  // Initialize countdown timers for drops
  document.querySelectorAll('[data-countdown-date]').forEach((element) => {
    const targetDate = element.dataset.countdownDate;
    new CountdownTimer(targetDate, `[data-countdown-date="${targetDate}"]`);
  });
});

/* ===================================================
   GLOBAL UTILITIES FOR SHOPIFY INTEGRATION
   =================================================== */

// Make utilities globally available
window.showToast = showToast;
window.CountdownTimer = CountdownTimer;
window.Cart = Cart;
window.ProductPage = ProductPage;
window.ScrollReveal = ScrollReveal;
window.Accordion = Accordion;
window.NewsletterForm = NewsletterForm;
window.AnnouncementBar = AnnouncementBar;
window.Search = Search;
window.LanguageToggle = LanguageToggle;
window.FormValidator = FormValidator;

// Expose utility functions
window.parseJSON = parseJSON;
window.throttle = throttle;
window.debounce = debounce;
window.isElementVisible = isElementVisible;
